"use client";

import { Suspense } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useSearchParams } from "next/navigation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@garagem/ui/components/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@garagem/ui/components/form";
import { PasswordInput } from "@garagem/ui/components/password-input";
import { Button } from "@garagem/ui/components/button";
import { toast } from "@garagem/ui/components/sonner";
import { api } from "@/lib/api/client";
import { useSignIn } from "@/hooks/auth";
import { authRedirectWithSession, getReturnUrl } from "@/lib/auth/redirect";
import { captureException } from "@web/lib/analytics/posthog-client";

const schema = z
  .object({
    password: z.string().min(8, "A senha deve ter pelo menos 8 caracteres"),
    confirmPassword: z.string().min(1, "Confirme sua senha"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "As senhas não coincidem",
    path: ["confirmPassword"],
  });

type FormData = z.infer<typeof schema>;

interface SetPasswordFormProps {
  /** Invitation token (invitation id) — authorizes setting the password. */
  token: string;
  /** Invitee email, shown read-only for context. */
  email: string | null;
  /** Switch to the sign-in tab (invitee already has a password). */
  onAlreadyHasPassword?: () => void;
  /** Switch to the sign-up tab (no user row exists yet for this email). */
  onNeedsSignup?: () => void;
}

function SetPasswordFormInner({
  token,
  email,
  onAlreadyHasPassword,
  onNeedsSignup,
}: SetPasswordFormProps) {
  const searchParams = useSearchParams();
  const returnUrl = getReturnUrl(searchParams || new URLSearchParams());
  const utils = api.useUtils();
  const setPassword = api.auth.setInvitationPassword.useMutation();
  const signIn = useSignIn();

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const isLoading = setPassword.isPending || signIn.isPending;

  const handleSubmit = async (formData: FormData) => {
    // Step 1: set the password. Failures here are genuine "couldn't set" errors.
    let result: Awaited<ReturnType<typeof setPassword.mutateAsync>>;
    try {
      result = await setPassword.mutateAsync({
        token,
        password: formData.password,
      });
    } catch (err) {
      captureException(err, {
        source: "set-password-form",
        component: "SetPasswordForm",
        action: "set-invitation-password",
      });
      toast.error("Erro ao definir senha", {
        description:
          err instanceof Error
            ? err.message
            : "Não foi possível definir sua senha. Tente novamente.",
      });
      return;
    }

    if (result.status === "already_set") {
      toast.info("Você já tem uma senha", {
        description: 'Use "Entrar". Se esqueceu, use "Esqueceu sua senha?".',
      });
      onAlreadyHasPassword?.();
      return;
    }

    if (result.status === "needs_signup") {
      toast.info("Crie sua conta", {
        description: "Preencha seus dados para concluir o cadastro.",
      });
      onNeedsSignup?.();
      return;
    }

    if (result.status === "invalid_invitation") {
      toast.error("Convite inválido ou expirado", {
        description: "Peça um novo convite ao administrador da organização.",
      });
      return;
    }

    // Password now exists — refresh the invitation lookup so the parent's
    // cached inviteeHasPassword flips to true. Otherwise a failed auto sign-in
    // would drop the user on "Entrar" while the banner still nudges "Definir
    // senha" (60s stale query that's never otherwise invalidated).
    void utils.auth.getInvitationDetails.invalidate({ token });

    // Step 2: password is set — sign in. A failure here must NOT read as
    // "couldn't set your password" (it's already set); send them to sign in
    // with the password they just chose.
    try {
      await signIn.mutateAsync({
        email: result.email,
        password: formData.password,
      });
    } catch (err) {
      captureException(err, {
        source: "set-password-form",
        component: "SetPasswordForm",
        action: "sign-in-after-set-password",
      });
      toast.info("Senha definida", {
        description: "Entre com sua nova senha para continuar.",
      });
      onAlreadyHasPassword?.();
      return;
    }

    // Signed in — wait for the session to be readable before redirecting so the
    // target page doesn't bounce back to login.
    toast.success("Senha definida com sucesso!", { description: "Entrando..." });
    await authRedirectWithSession(returnUrl);
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader>
        <CardTitle className="text-2xl">Definir senha</CardTitle>
        <CardDescription>
          {email ? (
            <>
              Crie uma senha para <strong>{email}</strong> e acesse sua conta.
            </>
          ) : (
            "Crie uma senha para acessar sua conta."
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form
            method="post"
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Senha</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      autoComplete="new-password"
                      placeholder="Mínimo 8 caracteres"
                      disabled={isLoading}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirmar senha</FormLabel>
                  <FormControl>
                    <PasswordInput
                      {...field}
                      autoComplete="new-password"
                      placeholder="Repita a senha"
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" disabled={isLoading} className="w-full">
              {isLoading ? "Definindo..." : "Definir senha e entrar"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

export function SetPasswordForm(props: SetPasswordFormProps) {
  return (
    <Suspense fallback={null}>
      <SetPasswordFormInner {...props} />
    </Suspense>
  );
}
